import { useQuery } from '@apollo/client/react';
import React from 'react';
import { FieldChangeGrid } from '@pages/admin/audit-log/change-grid';
import {
    getFieldChangeItems,
    getProcessedFieldChangeItems,
    removePropertiesFromObject,
} from '@pages/admin/audit-log/service';
import { useLocalState } from '@hooks/index';
import { GET_PANELS } from '@queries/central-monitoring';
import { auditLogsForStudy_OpraQueries_auditLogsForStudy_AuditRecord } from '@queries/generated/audit.generated';
import {
    getPanels_OpraQueries as getPanelsResponse,
    getPanelsVariables,
} from '@queries/generated/central-monitoring.generated';

interface RecordProps {
    PanelRefs: Array<unknown>;
}

interface StudyDashboardConfigPanelAuditLogGridProps {
    oldRecord: RecordProps;
    newRecord: RecordProps;
    oldItem?: Record<string, unknown>;
    newItem?: Record<string, unknown>;
    record: auditLogsForStudy_OpraQueries_auditLogsForStudy_AuditRecord;
}

const propArray = ['H', 'ConfigId'];

const StudyDashboardConfigPanelAuditLogGrid = ({
    oldRecord,
    newRecord,
    oldItem,
    newItem,
    record,
}: StudyDashboardConfigPanelAuditLogGridProps) => {
    const { state } = useLocalState();

    const { data: panels } = useQuery<getPanelsResponse, getPanelsVariables>(GET_PANELS, {
        variables: {
            organisationId: state?.selectedOrganisation?.id,
            studyId: record?.studyId,
            timestamp: newItem?.updated,
        },
        fetchPolicy: 'no-cache',
    });

    const formatPanelRefs = (recordData: unknown, index: number) => {
        if (!recordData) {
            return null;
        }

        if (recordData === 'SYS_SITESCORETREND') {
            return `${index + 1}: Site Score Trend`;
        }

        const panel = panels?.panels?.find((el) => el?.id === recordData);
        if (!panel) {
            return null;
        }
        return `${index + 1}: ${panel.recordId} ${panel.graph?.plotTitle}`;
    };

    const arrayOfObjectsV2: any = {
        PanelRefs: {
            valueResolver: formatPanelRefs,
        },
    };

    const refactorRecord = (auditRecord: Partial<RecordProps>) => {
        // Add empty WidgetIds array to keep arrayOfObjectsV2 happy
        if (!auditRecord.PanelRefs) {
            auditRecord.PanelRefs = [];
        }
        return {
            ...auditRecord,
            ...removePropertiesFromObject(auditRecord, propArray),
        } as Record<string, unknown>;
    };

    const refactoredOldRecord = refactorRecord(oldRecord);
    const refactoredNewRecord = refactorRecord(newRecord);

    const items = getFieldChangeItems(refactoredOldRecord, refactoredNewRecord, null, (field: string) =>
        getProcessedFieldChangeItems({
            oldRecord: refactoredOldRecord,
            newRecord: refactoredNewRecord,
            oldItem,
            newItem,
            field,
            arrayOfObjectsV2,
        }),
    );

    return <FieldChangeGrid items={items} />;
};

export default StudyDashboardConfigPanelAuditLogGrid;
